import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { PartiesClient, type PartiesSearch } from "@/features/parties/PartiesClient";
import { listParties } from "@/features/parties/parties.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { fetchSession, sessionQueryKey, hasAnyRole } from "@/lib/auth";

// Admin-only. The session is already cached by the _shell layout guard, so this
// resolves instantly; non-admins are bounced to the dashboard. This is an
// affordance/UX gate — the API enforces `requireRole("admin")` on writes regardless.
export const Route = createFileRoute("/_shell/parties")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!hasAnyRole(session, ["admin"])) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): PartiesSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    status:
      search.status === "active" || search.status === "inactive" ? search.status : undefined,
    page: typeof search.page === "string" ? Number(search.page) || undefined : undefined,
  }),
  component: PartiesPage,
});

function PartiesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["parties", search],
    queryFn: () =>
      listParties({
        q: search.q,
        isActive:
          search.status === "active" ? "true" : search.status === "inactive" ? "false" : undefined,
        page: search.page,
      }),
    placeholderData: keepPreviousData,
  });

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/parties",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
    });
  }

  return (
    <PartiesClient
      result={query.data}
      isLoading={query.isLoading}
      search={search}
      updateParams={updateParams}
    />
  );
}
